import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CalendarIcon, UsersIcon, CheckCircleIcon } from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';

const LandingPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    year_of_study: '',
    interests: [],
    share_profile: true
  });
  const [loading, setLoading] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);

  const availableInterests = ['Python', 'Trading', 'Hackathons', 'Startups', 'AI/ML', 'Web Dev'];

  const handleInterestToggle = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.year_of_study || formData.interests.length === 0) {
      alert('Please fill in all required fields and select at least one interest!');
      return;
    }

    setLoading(true);

    try {
      // Generate a random ID for the user
      const userData = {
        ...formData,
        id: Math.random().toString(36).substring(2, 15),
        created_at: new Date().toISOString()
      };

      // Save user profile to Firebase
      const docRef = await addDoc(collection(db, 'users'), userData);
      console.log('User profile saved to Firebase with ID:', docRef.id);

      // Save user profile to localStorage for backward compatibility
      localStorage.setItem('onetap_user_profile', JSON.stringify(userData));

      // Set submission complete to show the loading message
      setLoading(false);
      setSubmissionComplete(true);
    } catch (error) {
      console.error('Error saving user profile to Firebase:', error);
      alert('There was an error saving your profile. Please try again.');
      setLoading(false);
    }
  };

  // Loading screen for submission complete
  if (submissionComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
        {/* Blurred background content */}
        <div className="absolute inset-0 blur-overlay">
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="max-w-2xl w-full space-y-8">
              <div className="text-center space-y-6">
                <div className="flex items-center justify-center space-x-4 mb-3">
                  {/* Algosoc Logo */}
                  <img src="/AlgoSoc.png" alt="AlgoSoc Logo" className="w-10 h-10 object-contain rounded-xl" />
                  {/* WiT Logo */}
                  <img src="/Women in Trading.png" alt="Women in Trading Logo" className="w-10 h-10 object-contain rounded-xl" />
                  {/* OneTap Logo */}
                  <img src="/One-Tap.png" alt="OneTap Logo" className="w-10 h-10 object-contain rounded-xl" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold gradient-text">
                  Welcome to Algosoc
                </h1>
                <div className="glass p-8 rounded-3xl">
                  <p className="text-white/70">Algosoc community content loading...</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Loading overlay */}
        <div className="relative z-10 text-center space-y-6">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <CheckCircleIcon className="w-12 h-12 text-green-400" />
            <h1 className="text-4xl md:text-5xl font-bold gradient-text">
              Thank you!
            </h1>
          </div>

          <div className="space-y-4">
            <p className="text-xl text-white/90">
              OneTap loading.... Your profile will be ready soon!
            </p>
            <div className="flex items-center justify-center space-x-2">
              <div className="loading-dots w-2 h-2 bg-white rounded-full"></div>
              <div className="loading-dots w-2 h-2 bg-white rounded-full" style={{animationDelay: '0.2s'}}></div>
              <div className="loading-dots w-2 h-2 bg-white rounded-full" style={{animationDelay: '0.4s'}}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-2xl w-full space-y-8">
        {/* Hero Banner - OneTap Rebranded */}
        <div className="text-center space-y-6 fade-in">
          <div className="flex flex-col items-center justify-center mb-4">
            <div className="flex items-center justify-center space-x-4 mb-3">
              {/* Algosoc Logo */}
              <img src="/AlgoSoc.png" alt="AlgoSoc Logo" className="w-12 h-12 object-contain rounded-xl" />
              {/* WiT Logo */}
              <img src="/Women in Trading.png" alt="Women in Trading Logo" className="w-12 h-12 object-contain rounded-xl" />
              {/* OneTap Logo */}
              <img src="/One-Tap.png" alt="OneTap Logo" className="w-12 h-12 object-contain rounded-xl" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold gradient-text">
              Welcome to Algosoc
            </h1>
            <p className="text-white/80 mt-2">A collaborative project by Algosoc and OneTap</p>
          </div>

          <div className="space-y-3 text-white/90">
            <p className="text-lg flex items-center justify-center space-x-2">
              <UsersIcon className="w-5 h-5" />
              <span>Connect instantly with the wider community</span>
            </p>
            <p className="text-lg flex items-center justify-center space-x-2">
              <CalendarIcon className="w-5 h-5" />
              <span>One tap to join communities and discover events</span>
            </p>
          </div>
        </div>

        {/* Next Event Card */}
        <Card className="glass border-white/20 slide-up" data-testid="next-event-card">
          <CardHeader className="text-center">
            <CardTitle className="text-white text-xl flex items-center justify-center space-x-2">
              <span className="text-2xl">🚀</span>
              <span>Featured Event</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="text-white/90">
              <h3 className="font-semibold text-lg">Welcome Fair</h3>
              <p className="text-white/70">Wednesday, October 1, 2025 • All Day</p>
              <p className="text-white/60 text-sm mt-2">Powered by Algosoc</p>
            </div>
            <Button 
              className="btn-secondary"
              data-testid="add-to-calendar-btn"
              onClick={() => alert('Calendar integration coming soon!')}
            >
              Add to Calendar
            </Button>
          </CardContent>
        </Card>

        {/* Registration Form - OneTap Style */}
        <Card className="glass border-white/20 slide-up" data-testid="registration-form">
          <CardHeader className="text-center">
            <CardTitle className="text-white text-2xl mb-2">
              ⚡ Join the Algosoc Network
            </CardTitle>
            <p className="text-white/70">Connect with Algosoc and the wider community</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white font-medium">Name *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="form-input"
                  data-testid="name-input"
                  required
                />
              </div>

              {/* Year of Study */}
              <div className="space-y-2">
                <Label htmlFor="year" className="text-white font-medium">Year of Study *</Label>
                <Select 
                  value={formData.year_of_study} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, year_of_study: value }))}
                >
                  <SelectTrigger className="form-input" data-testid="year-select">
                    <SelectValue placeholder="Select your year" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900/95 backdrop-blur-md border-gray-700 shadow-xl z-50">
                    <SelectItem value="1st Year" className="text-white hover:bg-gray-700/80 focus:bg-gray-700/80 cursor-pointer">1st Year</SelectItem>
                    <SelectItem value="2nd Year" className="text-white hover:bg-gray-700/80 focus:bg-gray-700/80 cursor-pointer">2nd Year</SelectItem>
                    <SelectItem value="3rd Year" className="text-white hover:bg-gray-700/80 focus:bg-gray-700/80 cursor-pointer">3rd Year</SelectItem>
                    <SelectItem value="4th Year" className="text-white hover:bg-gray-700/80 focus:bg-gray-700/80 cursor-pointer">4th Year</SelectItem>
                    <SelectItem value="Graduate" className="text-white hover:bg-gray-700/80 focus:bg-gray-700/80 cursor-pointer">Graduate</SelectItem>
                    <SelectItem value="PhD" className="text-white hover:bg-gray-700/80 focus:bg-gray-700/80 cursor-pointer">PhD</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Interests */}
              <div className="space-y-3">
                <Label className="text-white font-medium">Interests * (Select at least one)</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {availableInterests.map((interest) => (
                    <Badge
                      key={interest}
                      variant="outline"
                      className={`interest-chip cursor-pointer ${formData.interests.includes(interest) ? 'selected' : ''}`}
                      onClick={() => handleInterestToggle(interest)}
                      data-testid={`interest-${interest.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                    >
                      {interest}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Consent */}
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="consent"
                  checked={formData.share_profile}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, share_profile: checked }))}
                  className="border-white/30 data-[state=checked]:bg-white data-[state=checked]:text-black"
                  data-testid="consent-checkbox"
                />
                <Label htmlFor="consent" className="text-white/90 text-sm cursor-pointer">
                  Share my profile with the community (opt-in)
                </Label>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                disabled={loading}
                className="btn-primary w-full"
                data-testid="join-network-btn"
              >
                {loading ? 'Connecting...' : '⚡ Join Algosoc\'s Network'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-white/60 text-sm">
          Powered by OneTap • Instant connections, lasting networks
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
